import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { smbRestartOrderResponseSchema, smbRestartOrderSchema } from "@shared/smb-admin";
import { smbPackageListResponseSchema, type smbPackageViewSchema } from "@shared/smb-packages";
import { apiRequest, parseJsonResponse } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { z } from "zod";

type Package = z.infer<typeof smbPackageViewSchema>;

type Props = {
  orderId: string;
  clientName: string;
  currentPackageId: string | null;
  disabled?: boolean;
  onCreated: (orderId: string) => void;
};

const restartRequestsInFlight = new Set<string>();

function namedError(error: unknown): { code: string; message: string } {
  const fallback = { code: "REQUEST_FAILED", message: "The new order could not be created. Retry to safely check the same request." };
  if (!(error instanceof Error)) return fallback;
  const match = error.message.match(/^\d{3}: ([\s\S]*)$/);
  if (!match) return { code: "REQUEST_FAILED", message: error.message || fallback.message };
  const status = Number(error.message.slice(0, 3));
  if (status === 401) return { code: "SESSION_EXPIRED", message: "Your session has expired. Sign in again, then retry this request." };
  if (status === 403) return { code: "FORBIDDEN", message: "You no longer have permission to start over. Check your SMB admin access before retrying." };
  try {
    const body: unknown = JSON.parse(match[1]);
    if (body && typeof body === "object" && "message" in body && typeof body.message === "string" && body.message.length <= 2000) {
      if ("code" in body && typeof body.code === "string") return { code: body.code, message: body.message };
      if (status !== 404) return { code: `HTTP_${status}`, message: body.message };
    }
  } catch { /* Keep malformed response bodies out of the dialog. */ }
  return status === 404
    ? { code: "ACTION_UNAVAILABLE", message: "The start-over action is not available on this server. Refresh the page after the server restarts, then retry." }
    : { ...fallback, code: `HTTP_${status}` };
}

function requestStorageKey(sourceId: string, packageId: string) {
  return `smb-order-restart:${sourceId}:${packageId}`;
}

export function RestartOrderDialog({ orderId, clientName, currentPackageId, disabled = false, onCreated }: Props) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const inFlight = useRef(false);
  const initialPackageChosen = useRef(false);
  const packagesQuery = useQuery({
    queryKey: ["/api/admin/smb/packages", page, 100],
    queryFn: async () => smbPackageListResponseSchema.parse(
      await parseJsonResponse(await apiRequest("GET", `/api/admin/smb/packages?page=${page}&pageSize=100`)),
    ),
    enabled: open,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    gcTime: 0,
  });
  const activePackages = packagesQuery.data?.packages.filter(item => item.active && !item.pausedReason) ?? [];

  useEffect(() => {
    if (!open || initialPackageChosen.current || !packagesQuery.data || page !== 1) return;
    const available = packagesQuery.data.packages.filter(item => item.active && !item.pausedReason);
    if (available.length) {
      setSelectedPackage(available.find(item => item.id !== currentPackageId) ?? available[0]);
    }
    initialPackageChosen.current = true;
  }, [open, page, packagesQuery.data, currentPackageId]);

  async function submit() {
    if (inFlight.current || !selectedPackage) return;
    const fenceKey = `${orderId}:${selectedPackage.id}`;
    if (restartRequestsInFlight.has(fenceKey)) return;
    inFlight.current = true;
    restartRequestsInFlight.add(fenceKey);
    setPending(true);
    setError(null);
    try {
      const storageKey = requestStorageKey(orderId, selectedPackage.id);
      let requestId: string | null;
      try {
        requestId = sessionStorage.getItem(storageKey);
        if (!requestId) {
          requestId = crypto.randomUUID();
          sessionStorage.setItem(storageKey, requestId);
        }
      } catch {
        throw new Error("A secure retry key could not be saved in this browser session. Enable session storage and try again.");
      }
      const body = smbRestartOrderSchema.parse({ packageId: selectedPackage.id, requestId });
      const result = smbRestartOrderResponseSchema.parse(await parseJsonResponse(
        await apiRequest("POST", `/api/admin/smb/orders/${encodeURIComponent(orderId)}/restart`, body),
      ));
      try { sessionStorage.removeItem(storageKey); } catch { /* Parsed success is authoritative; storage cleanup is best effort. */ }
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ["/api/admin/smb/board"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/admin/smb/orders"] }),
      ]);
      setOpen(false);
      onCreated(result.order.id);
    } catch (cause) {
      setError(namedError(cause));
    } finally {
      inFlight.current = false;
      restartRequestsInFlight.delete(fenceKey);
      setPending(false);
    }
  }

  const selectedOnPage = activePackages.some(item => item.id === selectedPackage?.id);
  const totalPages = Math.max(1, Math.ceil((packagesQuery.data?.total ?? 0) / 100));

  return <>
    <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => { setError(null); setOpen(true); }}>
      Change package and start over
    </Button>
    <Dialog open={open} onOpenChange={nextOpen => { if (!inFlight.current) { setOpen(nextOpen); if (!nextOpen) setError(null); } }}>
      <DialogContent className="max-h-[90dvh] w-[calc(100%-2rem)] overflow-y-auto sm:max-w-xl">
        <div className="space-y-2">
          <Badge variant="secondary">Create a separate order</Badge>
          <DialogTitle>Change package and start over</DialogTitle>
          <DialogDescription className="leading-relaxed">
            A fresh order will be created for <strong className="font-semibold text-foreground">{clientName}</strong>.
            The original order, its history, assets, and recorded cost stay unchanged.
          </DialogDescription>
        </div>
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm leading-relaxed">
          This only creates the order record. Research and paid generation will not run automatically; review the new order before starting any work.
        </div>

        <div className="space-y-2">
          <div className="flex items-baseline justify-between gap-3">
            <label htmlFor="restart-package" className="text-sm font-medium">Choose an active package</label>
            <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
          </div>
          <select
            id="restart-package"
            aria-label="Active package"
            value={selectedOnPage ? selectedPackage!.id : ""}
            onChange={event => setSelectedPackage(activePackages.find(item => item.id === event.target.value) ?? null)}
            disabled={packagesQuery.isLoading || packagesQuery.isError || pending}
            className="h-10 w-full rounded-md border bg-background px-3 text-sm"
          >
            <option value="">Select a package</option>
            {activePackages.map(item => <option key={item.id} value={item.id}>{item.name}{item.isDefault ? " · default" : ""}</option>)}
          </select>
          <div className="flex items-center justify-between gap-2">
            <Button type="button" size="sm" variant="ghost" disabled={page <= 1 || pending} onClick={() => setPage(value => Math.max(1, value - 1))}>Previous</Button>
            <span className="text-xs text-muted-foreground">
              {packagesQuery.data ? `${packagesQuery.data.total} packages · ${activePackages.length} active on this page` : "Packages are listed 100 at a time"}
            </span>
            <Button type="button" size="sm" variant="ghost" disabled={page >= totalPages || pending} onClick={() => setPage(value => Math.min(totalPages, value + 1))}>Next</Button>
          </div>
          {packagesQuery.isLoading && <div role="status" aria-label="Loading packages" className="h-10 animate-pulse rounded-md bg-muted/70" />}
          {packagesQuery.isError && <div role="alert" className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
            <span>Active packages could not be loaded.</span>
            <Button type="button" variant="outline" size="sm" disabled={packagesQuery.isFetching} onClick={() => void packagesQuery.refetch()}><RefreshCw className={cn("mr-1.5 h-3.5 w-3.5", packagesQuery.isFetching && "animate-spin")} />Retry</Button>
          </div>}
          {packagesQuery.data && activePackages.length === 0 && <p className="rounded-md border p-3 text-sm text-muted-foreground">There are no active packages on this page. Use Next to check the remaining pages.</p>}
          {selectedPackage && <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
            <span className="text-muted-foreground">Selected: </span><strong className="font-medium">{selectedPackage.name}</strong>
            <span className="ml-2 text-xs text-muted-foreground">{selectedPackage.id === currentPackageId ? "Same as current" : "Different from current"}</span>
            {selectedPackage.id === currentPackageId && <p className="mt-1 text-xs text-muted-foreground">Current package: {selectedPackage.name}</p>}
          </div>}
          {currentPackageId && selectedPackage?.id !== currentPackageId && <p className="text-xs text-muted-foreground">Current package: {packagesQuery.data?.packages.find(item => item.id === currentPackageId)?.name ?? "A different package"}</p>}
        </div>

        {error && <div role="alert" className="space-y-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
          <p className="flex items-center gap-2 font-medium text-destructive"><AlertTriangle className="h-4 w-4 shrink-0" />{error.message}</p>
          <p className="text-xs text-muted-foreground">Error code: {error.code}. Retry reuses this source-and-package request key; no automatic retry was made.</p>
        </div>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" disabled={pending} onClick={() => { setOpen(false); setError(null); }}>Cancel</Button>
          <Button type="button" disabled={pending || packagesQuery.isLoading || packagesQuery.isError || !selectedPackage} onClick={() => void submit()}>
            {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{error ? "Retry same request" : "Confirm new order"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  </>;
}
